import { ErrorTemplateBuilder } from "../../infrastructure/error-template-builder";
import { EnumConfig } from "../entity-process/signalr-enums-config";
import type {
  CustomProperties,
  entityProcessConfig,
  entityProcessDictionary,
} from "../entity-process/signalr-process-config";
import { Enums } from "../helper/enums";

export class EntityManager {
  //#region Entity Enums
  static entityProcessDic: entityProcessDictionary = EnumConfig;
  //#endregion

  static async updateEntity(
    message: any,
    methodName: string,
    identifier: string,
    param: any,
    customProperties: CustomProperties[] = [],
    highLightedProperty: boolean = false,
  ): Promise<[any, entityProcessConfig | null]> {
    const processObjec = EntityManager.entityProcessDic[methodName];
    try {
      if (
        message.eventId === Enums.StateEntitySync.InsertOrUpdate ||
        message.eventId == Enums.StateEntitySync.Reload
      ) {
        const result = await this.insertOrUpdateEntity(
          processObjec,
          identifier,
          param,
          message,
        );
        return [result, processObjec];
      } else if (message.eventId == Enums.StateEntitySync.Delete) {
        const objectFound = processObjec.entity!.find(message.entityId);
        if (objectFound) {
          await processObjec.entity!.delete(message.entityId);
        }
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText("EntityManager.updateEntity", e),
      );
    }

    return [null, processObjec];
  }

  static async insertOrUpdateEntity(
    processObjec: entityProcessConfig,
    identifier: string,
    param: any,
    message: any,
  ) {
    try {
      if (message.data) {
        let isUpdated = await this._insertOrUpdateEntity(
          processObjec.entity,
          message.data,
          identifier
            ? (entity: any) => {
                return entity[identifier] == message.entityId;
              }
            : undefined,
        );

        if (!isUpdated && this.canICallApi(processObjec)) {
          let response = await processObjec.promise!(param);
          if (response) {
            this._insertOrUpdateEntity(processObjec.entity, response);
            return [response, processObjec];
          }

          return [response, processObjec];
        }

        return [null, processObjec];
      } else if (this.canICallApi(processObjec)) {
        let responseone: any[] = await processObjec.promise!(param);
        if (responseone) {
          await this._insertOrUpdateEntity(
            processObjec.entity,
            responseone,
            undefined,
          );
          return [responseone, processObjec];
        }
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "EntityManager.insertOrUpdateEntity",
          e,
        ),
      );
    }
  }

  //#region Private Methods

  private static async _insertOrUpdateEntity(
    entity: any,
    data: any,
    where?: (entity: any) => boolean,
  ): Promise<boolean> {
    try {
      if (where) {
        let result = await entity!.update({
          where: where,
          data: data,
        });

        return result && result.length > 0 ? true : false;
      } else {
        let result = await entity!.insertOrUpdate({
          data: data,
        });

        return result && result.length > 0 ? true : false;
      }
    } catch (ex) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "EntityManager._insertOrUpdateEntity",
          ex,
        ),
      );
    }
  }

  private static canICallApi(processObjec: entityProcessConfig) {
    return processObjec && processObjec.hasAccess() && processObjec.promise;
  }

  //#endregion

  //#region Others Methods
  static async updateSingleLocalEntityBySyntax(
    message: any,
    methodName: string,
    identifier: string,
    param: any,
    responseIdentifierData: any,
    responseIdentifierValue: any,
  ): Promise<boolean> {
    try {
      const processObjec = EntityManager.entityProcessDic[methodName];
      if (message.eventId === Enums.StateEntitySync.InsertOrUpdate) {
        if (processObjec && processObjec.hasAccess()) {
          let result: any[] | null = null;
          if (identifier && responseIdentifierData) {
            result = await processObjec.entity.update({
              where: (entity: any) => {
                return entity[identifier] == param;
              },
              data: (data: any) => {
                data[responseIdentifierData] = responseIdentifierValue;
              },
            });
          } else if (identifier && responseIdentifierData == "") {
            result = await processObjec.entity.update({
              where: (entity: any) => {
                return entity[identifier] == param;
              },
              data: responseIdentifierValue,
            });
          }
          return result && result.length > 0 ? true : false;
        }
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "EntityManager.updateSingleLocalEntityBySyntax",
          e,
        ),
      );
    }
    return false;
  }

  static async getOnlyAPCallForEntity(
    message: any,
    methodName: string,
    param: any,
  ): Promise<[any, entityProcessConfig | null]> {
    const processObjec = EntityManager.entityProcessDic[methodName];
    try {
      if (message.eventId === Enums.StateEntitySync.InsertOrUpdate) {
        if (this.canICallApi(processObjec)) {
          let response = await processObjec.promise!(param);
          return [response, processObjec];
        }
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "EntityManager.getOnlyAPCallForEntity",
          e,
        ),
      );
    }

    return [null, processObjec];
  }

  static checkEnumStateType(message: any): boolean {
    if (
      message.eventId == Enums.StateEntitySync.InsertOrUpdate ||
      message.eventId == Enums.StateEntitySync.Delete ||
      message.eventId == Enums.StateEntitySync.CheckStatusUpdate
    ) {
      return true;
    }
    return false;
  }

  static async deleteFromEntity(
    methodName: string,
    identifier: string = "",
    comparator: any = null,
    entityId: any = null,
  ) {
    const processObjec = EntityManager.entityProcessDic[methodName];
    try {
      if (processObjec && processObjec.hasAccess()) {
        if (identifier != "" && comparator) {
          //Handles Single Conditions Only
          await processObjec.entity!.delete((each: any) => {
            return each[identifier] == comparator;
          });
        } else if (entityId) {
          //Delete Using PrimaryKey
          await processObjec.entity!.delete(entityId);
        } else {
          //If Either of the above conditions Fail
          throw "Failed to delete from entity";
        }
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText("EntityManager.deleteFromEntity", e),
      );
    }
  }

  //#endregion
}
